const SIBLING_COUNT = 2;
const LONG_STEP_PREV = 5;
const LONG_STEP_NEXT = 10;
const MIN_PAGE_COUNT = 10;
const FIRST_PAGE = { page: 1 };

interface PageObject {
  page: number;
  dotsType?: 'left' | 'right';
}

interface Props {
  currentPage: number;
  totalPageCount: number;
  isBreakpointSM: boolean;
}

const getRange = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, index) => {
    const pageNumber = index + start;

    return { page: pageNumber };
  });
};

export const usePaginationRange = ({ currentPage, totalPageCount, isBreakpointSM }: Props): PageObject[] => {
  const lastPageObject = { page: totalPageCount };

  if (isBreakpointSM) {
    return [...getRange(1, totalPageCount)];
  }

  const leftDotsObject = {
    dotsType: 'left',
    page: Math.max(1, currentPage - LONG_STEP_PREV),
  };
  const rightDotsObject = {
    dotsType: 'right',
    page: Math.min(totalPageCount, currentPage + LONG_STEP_NEXT),
  };

  const leftSiblingPageCount = Math.max(currentPage - SIBLING_COUNT - SIBLING_COUNT, 1);
  const rightSiblingPageCount = Math.min(currentPage + SIBLING_COUNT, totalPageCount);

  const shouldShowLeftDots = leftSiblingPageCount > SIBLING_COUNT && totalPageCount > MIN_PAGE_COUNT;
  const shouldShowRightDots = rightSiblingPageCount < totalPageCount - 1 && totalPageCount > MIN_PAGE_COUNT;
  const shouldShowOnlyRightDots = !shouldShowLeftDots && shouldShowRightDots;
  const shouldShowOnlyLeftDots = shouldShowLeftDots && !shouldShowRightDots;
  const shouldShowBothDots = shouldShowLeftDots && shouldShowRightDots;

  if (shouldShowOnlyRightDots) {
    const leftRange = getRange(1, 7);

    return [...leftRange, rightDotsObject, lastPageObject];
  }

  if (shouldShowOnlyLeftDots) {
    const rightRange = getRange(totalPageCount - LONG_STEP_PREV, totalPageCount);

    return [FIRST_PAGE, leftDotsObject, ...rightRange];
  }

  if (shouldShowBothDots) {
    const middleRange = getRange(leftSiblingPageCount + SIBLING_COUNT, rightSiblingPageCount);

    return [FIRST_PAGE, leftDotsObject, ...middleRange, rightDotsObject, lastPageObject];
  }

  return [...getRange(1, totalPageCount)];
};
